import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RT, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line, AreaChart, Area
} from "recharts";
import { Sun, Wrench, TrendingUp, Users, IndianRupee, ClipboardCheck, AlertTriangle, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { DASH } from "@/constants/testIds";
import ChartInsightButton from "@/components/insights/ChartInsightButton";

const COLORS = ["#00A859","#0A192F","#FF9933","#0EA5E9","#8B5CF6","#EAB308","#EC4899","#10B981"];

function KPI({ label, value, delta, icon: Icon, tone = "primary" }) {
  return (
    <div className="kpi-card" data-testid={DASH.kpiCard}>
      <div className="flex items-start justify-between">
        <div className={`h-10 w-10 rounded-lg grid place-items-center bg-[hsl(var(--${tone}))]/10 text-[hsl(var(--${tone}))]`}>
          <Icon className="h-5 w-5"/>
        </div>
        {delta && <div className="badge-status-open"><TrendingUp className="h-3 w-3"/>{delta}</div>}
      </div>
      <div className="mt-4 font-heading text-2xl lg:text-3xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

export default function ExecutiveDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/executive").then((r) => setD(r.data)).catch(()=>{}); }, []);

  if (!d) return <div className="text-sm text-muted-foreground">Loading executive insights…</div>;

  // fake monthly funds series
  const funds = [
    { m: "Aug", allocated: 120, spent: 82 }, { m: "Sep", allocated: 140, spent: 98 },
    { m: "Oct", allocated: 160, spent: 121 }, { m: "Nov", allocated: 155, spent: 138 },
    { m: "Dec", allocated: 180, spent: 149 }, { m: "Jan", allocated: 210, spent: 168 },
  ];
  const alerts = [
    { t: "Bathinda 50 MW Solar – 12 days behind schedule", tone: "amber" },
    { t: "Vendor bill pending > 30 days: Bhullar Constructions", tone: "red" },
    { t: "5 grievances awaiting closure past SLA", tone: "amber" },
    { t: "AMC expiring in Feb – 3 IT assets", tone: "sky" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="label-tiny text-[hsl(var(--primary))]">Executive Dashboard</div>
          <h1 className="mt-1 text-3xl font-heading font-bold">Punjab Renewable Portfolio</h1>
          <div className="text-xs text-muted-foreground mt-1">Live snapshot · FY 2025-26 · {new Date().toLocaleDateString()}</div>
        </div>
        <div className="flex gap-2">
          <Link to="/app/works" className="btn-outline">Works View</Link>
          <Link to="/gis" className="btn-primary">Open GIS Map</Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Total Projects" value={d.kpis.total_projects} delta="+8%" icon={Wrench} tone="primary"/>
        <KPI label="Solar MW Installed" value={`${d.kpis.total_solar_capacity_mw}`} delta="+12%" icon={Sun} tone="accent"/>
        <KPI label="Citizen Applications" value={d.kpis.total_applications} delta="+22%" icon={Users} tone="primary"/>
        <KPI label="Budget Utilization" value={`${d.kpis.budget_utilization_pct}%`} delta="On track" icon={IndianRupee} tone="primary"/>
        <KPI label="Open Tenders" value={d.kpis.tenders_open} icon={ClipboardCheck} tone="primary"/>
        <KPI label="Open Grievances" value={d.kpis.grievances_open} icon={AlertTriangle} tone="accent"/>
        <KPI label="Total Budget" value={`₹${d.kpis.total_budget_cr} Cr`} icon={IndianRupee} tone="primary"/>
        <KPI label="Total Spent" value={`₹${d.kpis.total_spent_cr} Cr`} icon={Activity} tone="accent"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-5 relative" data-testid={DASH.chart}>
          <div className="flex items-center justify-between">
            <div>
              <div className="label-tiny">Funds Utilization</div>
              <div className="font-heading font-semibold">Allocated vs Spent (₹ Cr)</div>
            </div>
            <div className="flex items-center gap-2">
              <ChartInsightButton title="Funds Utilization — Allocated vs Spent (₹ Cr, last 6 months)" data={funds} />
              <div className="text-xs text-muted-foreground">Last 6 months</div>
            </div>
          </div>
          <div className="h-64 mt-4">
            <ResponsiveContainer>
              <AreaChart data={funds}>
                <defs>
                  <linearGradient id="a1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00A859" stopOpacity={0.35}/>
                    <stop offset="100%" stopColor="#00A859" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="a2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF9933" stopOpacity={0.35}/>
                    <stop offset="100%" stopColor="#FF9933" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
                <XAxis dataKey="m" stroke="hsl(var(--muted-foreground))" fontSize={12}/>
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12}/>
                <RT contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}/>
                <Area type="monotone" dataKey="allocated" stroke="#00A859" fill="url(#a1)" strokeWidth={2}/>
                <Area type="monotone" dataKey="spent" stroke="#FF9933" fill="url(#a2)" strokeWidth={2}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 relative" data-testid={DASH.chart}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="label-tiny">Projects by Type</div>
              <div className="font-heading font-semibold">Portfolio mix</div>
            </div>
            <ChartInsightButton title="Projects by Type — Portfolio Mix" data={d.by_type} />
          </div>
          <div className="h-64 mt-4">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={d.by_type} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {d.by_type.map((_, i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                </Pie>
                <RT/>
                <Legend wrapperStyle={{ fontSize: 11 }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-5 relative">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="label-tiny">Districts – Project Count</div>
              <div className="font-heading font-semibold">Top by activity</div>
            </div>
            <ChartInsightButton title="Districts – Project Count (Top 12)" data={d.by_district.slice(0, 12)} />
          </div>
          <div className="h-64 mt-4">
            <ResponsiveContainer>
              <BarChart data={d.by_district.slice(0, 12)}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={10} interval={0} angle={-20} textAnchor="end" height={60}/>
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12}/>
                <RT contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}/>
                <Bar dataKey="value" fill="#0A192F" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="label-tiny">Critical Alerts</div>
          <div className="font-heading font-semibold">Attention needed</div>
          <div className="mt-4 space-y-2">
            {alerts.map((a, i) => (
              <div key={i} className={`rounded-lg p-3 text-sm ${a.tone === "red" ? "bg-red-500/10 text-red-600 dark:text-red-400" : a.tone === "amber" ? "bg-amber-500/10 text-amber-600 dark:text-amber-400" : "bg-sky-500/10 text-sky-600 dark:text-sky-400"}`}>
                <AlertTriangle className="h-3.5 w-3.5 inline mr-1.5"/>{a.t}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-tiny">Projects</div>
            <div className="font-heading font-semibold">Latest snapshot</div>
          </div>
          <Link to="/app/works" className="btn-outline text-xs">View all</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-th">Project</th>
                <th className="table-th">Type</th>
                <th className="table-th">District</th>
                <th className="table-th">Budget</th>
                <th className="table-th">Progress</th>
                <th className="table-th">Status</th>
              </tr>
            </thead>
            <tbody>
              {d.projects.slice(0, 10).map((p) => (
                <tr key={p.id} className="border-t border-border">
                  <td className="table-cell font-medium">{p.name}</td>
                  <td className="table-cell capitalize text-muted-foreground">{p.project_type.replace("_"," ")}</td>
                  <td className="table-cell">{p.district}</td>
                  <td className="table-cell">₹{(p.budget/1e5).toFixed(1)}L</td>
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-[hsl(var(--primary))]" style={{width:`${p.progress}%`}}/></div>
                      <span className="text-xs">{p.progress}%</span>
                    </div>
                  </td>
                  <td className="table-cell"><span className="badge-status-approved capitalize">{p.status.replace("_"," ")}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
